import type { GameId, Lang, RoomView } from "@shared/types";
import { ROOM_GAMES } from "@shared/types";
import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import { Btn } from "../components/Button";
import { Segmented } from "../components/Controls";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { Bot, Clock, Smartphone, Users, Wifi } from "../components/Icon";
import { IdentityForm, type Identity } from "../components/IdentityForm";
import { LoopVideo } from "../components/LoopVideo";
import { toast } from "../components/Toast";
import { gameMeta } from "../games/meta";
import { UI_LANGS, useI18n } from "../i18n";
import { api, errorCode } from "../lib/api";
import { IS_DEMO } from "../lib/asset";
import { useAuth } from "../lib/auth";
import { createLocal } from "../lib/local-registry";
import { me, saveSeat } from "../lib/prefs";
import { useStore } from "../lib/store";
import { NotFound } from "./NotFound";

type Mode = "online" | "bots" | "device";
const MODES: Record<GameId, Mode[]> = {
  bluff: ["online", "bots"],
  spy: ["online", "device"],
  match: ["online", "device", "bots"],
  truf: ["online", "bots"],
  guess: ["online"],
};

export function GamePage({ game }: { game: string }) {
  // Guess has its own page (one phone, or an online room started from there)
  if (game === "guess") return <Redirect to="~/guess" replace />;
  if (!ROOM_GAMES.includes(game as GameId)) return <NotFound />;
  return <GameLanding game={game as GameId} />;
}

function GameLanding({ game }: { game: GameId }) {
  const { d, lang: uiLang } = useI18n();
  const [, nav] = useLocation();
  const { user } = useAuth();
  const saved = useStore(me);
  const g = gameMeta(game);
  const t = d.games[game];
  const [id, setId] = useState<Identity>({ name: saved.name || user?.displayName || "", avatar: saved.avatar ?? user?.avatar ?? Math.floor(Math.random() * 19) });
  const [lang, setLang] = useState<Lang>(uiLang);
  const modes = MODES[game];
  const [mode, setMode] = useState<Mode>(IS_DEMO ? modes.find((m) => m !== "online") ?? "online" : "online");
  const [busy, setBusy] = useState(false);

  async function go() {
    const name = id.name.trim();
    if (!name) return toast(d.create.nameRequired, "bad");
    me.set({ name, avatar: id.avatar });
    setBusy(true);
    try {
      if (mode === "device" || (mode === "bots" && IS_DEMO)) {
        const players = [{ name, avatar: id.avatar }];
        const room = await createLocal({ game, lang, players });
        if (mode === "bots") for (let i = 0; i < 3; i++) await room.act({ t: "addBot" });
        nav(`/local/${room.code}`);
        return;
      }
      const r = await api<{ ok: true; code: string; playerId: string; token: string; view: RoomView }>("/rooms", {
        method: "POST",
        body: { game, lang, name, avatar: id.avatar },
      });
      saveSeat(r.code, { playerId: r.playerId, token: r.token, name });
      if (mode === "bots") {
        for (let i = 0; i < 3; i++) {
          await api(`/rooms/${r.code}/act`, { method: "POST", body: { playerId: r.playerId, action: { t: "addBot" } }, headers: { "x-player-token": r.token } });
        }
      }
      nav(`/room/${r.code}`);
    } catch (e) {
      const c = errorCode(e);
      toast((d.err as Record<string, string>)[c] ?? (c === "demo" ? d.demo.serverOnly : d.err.server), "bad");
    } finally {
      setBusy(false);
    }
  }

  const modeInfo: Record<Mode, { title: string; sub: string; icon: React.ReactNode }> = {
    online: { title: d.create.online, sub: d.create.onlineSub, icon: <Wifi size={22} /> },
    device: { title: d.create.local, sub: d.create.localSub, icon: <Smartphone size={22} /> },
    bots: { title: d.create.solo, sub: d.create.soloSub, icon: <Bot size={22} /> },
  };

  return (
    <div className="min-h-dvh">
      <Header />
      <main>
        <section style={{ background: `color-mix(in oklab, ${g.color} 14%, var(--color-ground))` }}>
          <div className="shell grid gap-8 py-10 md:grid-cols-[1fr_1.1fr] md:items-center md:py-16">
            <div className="relative aspect-square w-full max-w-[460px] overflow-hidden rounded-[32px]" style={{ background: g.deep }}>
              <LoopVideo src={g.video} poster={g.poster} className="h-full w-full object-cover" alt={t.name} />
            </div>
            <div>
              <h1 className="display text-[clamp(44px,7vw,84px)]" style={{ color: g.color }}>{t.name}</h1>
              <p className="mt-4 text-2xl font-extrabold">{t.tagline}</p>
              <p className="mt-4 max-w-xl text-lg text-ink-2">{t.short}</p>
              <p className="mt-5 flex flex-wrap gap-x-6 gap-y-1 font-bold text-ink-2">
                <span className="inline-flex items-center gap-1.5"><Users size={18} /> {t.players}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={18} /> {d.common.minutes.replace("{n}", t.time)}</span>
              </p>
            </div>
          </div>
        </section>

        <section className="shell grid gap-10 py-12 lg:grid-cols-[1.15fr_1fr]">
          <div className="surface p-6 sm:p-8">
            <h2 className="display text-3xl">{d.create.title}</h2>
            <div className="mt-6 grid gap-3">
              {modes.map((m) => {
                const disabled = IS_DEMO && m === "online";
                const on = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={disabled}
                    onClick={() => setMode(m)}
                    className="tile flex items-center gap-4 px-5 py-4"
                    style={{ ["--b" as string]: on ? g.color : "var(--color-ground-3)", color: on ? g.ink : undefined, opacity: disabled ? 0.45 : 1 }}
                    aria-pressed={on}
                  >
                    <span className="shrink-0">{modeInfo[m].icon}</span>
                    <span>
                      <span className="block text-lg font-extrabold">{modeInfo[m].title}</span>
                      <span className="block text-sm opacity-80">{disabled ? d.demo.serverOnly : modeInfo[m].sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-8">
              <IdentityForm value={id} onChange={setId} />
            </div>
            <div className="mt-6">
              <span className="label">{d.create.roomLang}</span>
              <Segmented<Lang> value={lang} onChange={(l) => setLang(l)} options={UI_LANGS.map((l) => ({ value: l, label: d.common.lang[l] }))} />
            </div>
            <div className="mt-8">
              <Btn game={game} size="lg" block disabled={busy} onClick={go} sound="pop">
                {d.create.go}
              </Btn>
            </div>
          </div>
          <div>
            <h2 className="display text-3xl">{d.create.howToPlay}</h2>
            <ul className="mt-6 space-y-5">
              {t.rules.map((r, i) => (
                <li key={i} className="flex gap-4 text-lg">
                  <span className="mt-2 h-3 w-3 shrink-0 rounded-full" style={{ background: g.color }} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
